import type {
  ClassifiedChapterBeat,
  DramaticChapterBeat,
  FourBeatChapterContract,
  FourBeatGateResult,
  GeneratedChapterBeat,
  GeneratedFourBeatChapter,
} from '../types';
import { FourBeatStructuralFailureError } from '../types';
import {
  assembleFourBeatChapter,
  buildFourBeatAssemblyDiagnostic,
  parseFourBeatResponseV1,
} from './fourBeatChapterAssembly.service';
import {
  buildAtomicEventDistributionDiagnostic,
  buildFourBeatContractPrompt,
} from './fourBeatChapterContract.service';
import {
  classifySingleGeneratedBeat,
  collectPreInspectorRepairCandidates,
  evaluateFourBeatGate,
} from './fourBeatChapterValidation.service';
import {
  FOUR_BEAT_MAX_REPAIR_CALLS,
  FOUR_BEAT_MAX_REPAIR_PER_BEAT,
} from './scribeThinkingPolicy.service';

export interface BeatRepairRequest {
  contract: FourBeatChapterContract;
  contractBeat: DramaticChapterBeat;
  deficientBeat: GeneratedChapterBeat;
  classifiedBeat: ClassifiedChapterBeat;
  previousAcceptedEndingState?: string;
  nextBeatOpeningState?: string;
  storyContext: string;
}

export interface BeatRepairResult {
  beat: GeneratedChapterBeat;
}

export function buildBeatRepairPrompt(request: BeatRepairRequest): string {
  const { contractBeat, deficientBeat, classifiedBeat, previousAcceptedEndingState, nextBeatOpeningState } = request;

  return `
TARGETED BEAT REPAIR — rewrite ONE beat only.

Beat ID (must preserve exactly): ${contractBeat.beatId}
Sequence: ${contractBeat.sequence} of 4
Function: ${contractBeat.function}

VALIDATION FAILURES TO FIX:
${classifiedBeat.issues
  .filter((issue) => issue.severity === 'REPAIR')
  .map((issue) => `- ${issue.code}: ${issue.message}`)
  .join('\n')}

BEAT CONTRACT:
${buildFourBeatContractPrompt({
  ...request.contract,
  beats: request.contract.beats,
}).split('BEAT ').find((section) => section.startsWith(String(contractBeat.sequence))) || ''}

PREVIOUS ACCEPTED ENDING STATE:
${previousAcceptedEndingState || contractBeat.openingState}

NEXT BEAT REQUIRED OPENING:
${nextBeatOpeningState || contractBeat.transitionToNextBeat}

DEFICIENT BEAT TO REPLACE (do not reuse summary compression):
${deficientBeat.content}

TARGET WORD RANGE: ${contractBeat.targetMinWords}–${contractBeat.targetMaxWords}
STRUCTURAL FLOOR: ${contractBeat.acceptanceFloorWords}

Return JSON for this beat only. Preserve beatId=${contractBeat.beatId}. Report satisfied atomic event IDs in requiredEventsSatisfied.
`.trim();
}

export async function executeFourBeatGenerationGate(params: {
  chapterId: number;
  contract: FourBeatChapterContract;
  title: string;
  summary: string;
  initialJson: {
    title?: string;
    summary?: string;
    beats?: Array<{
      beatId?: string;
      sequence?: number;
      content?: string;
      endingState?: string;
      requiredEventsSatisfied?: string[];
      decisionPresent?: boolean;
      consequencePresent?: boolean;
    }>;
    scenes?: string[];
  };
  responseMeta?: { finishReason?: string; outputTokenCount?: number };
  repairBeat?: (request: BeatRepairRequest) => Promise<BeatRepairResult>;
  onDeveloperLog?: (tag: string, payload: unknown) => void;
  onUserProgress?: (message: string) => void;
}): Promise<{ generated: GeneratedFourBeatChapter; gate: FourBeatGateResult }> {
  const {
    chapterId,
    contract,
    initialJson,
    responseMeta,
    repairBeat,
    onDeveloperLog,
    onUserProgress,
  } = params;

  let repairCallsUsed = 0;
  const repairsAttempted = new Map<string, number>();

  onDeveloperLog?.('[ATOMIC EVENT DISTRIBUTION]', buildAtomicEventDistributionDiagnostic(contract.atomicEvents));

  const parseInitial = () => {
    const parsed = parseFourBeatResponseV1(chapterId, initialJson, responseMeta);
    if (parsed.ok) {
      onDeveloperLog?.('[FOUR BEAT RESPONSE FORMAT]', {
        format: 'FOUR_BEAT_V1',
        status: 'valid',
        beatCount: parsed.generated.beats.length,
      });
      return parsed.generated;
    }

    onDeveloperLog?.('[FOUR BEAT RESPONSE FORMAT]', {
      format: 'FOUR_BEAT_V1',
      status: 'invalid',
      reason: parsed.reason,
      recoverable: Boolean(parsed.recoverableBeats),
      scenesPresent: Boolean(initialJson.scenes?.length),
    });

    if (parsed.recoverableBeats) {
      onDeveloperLog?.('[FOUR BEAT RESPONSE FORMAT]', {
        status: 'recovered_from_malformed_beats',
        beatCount: parsed.recoverableBeats.length,
      });
      return assembleFourBeatChapter({
        chapterId,
        title: initialJson.title || params.title,
        summary: initialJson.summary || params.summary,
        beats: parsed.recoverableBeats,
      });
    }

    throw new FourBeatStructuralFailureError(parsed.reason, {
      chapterNumber: chapterId,
      gate: {
        accepted: false,
        structurallyValid: false,
        allBeatsAccepted: false,
        allFourBeatsPresent: false,
        allBeatIdsUnique: false,
        beats: [],
        issues: [],
        warnings: [],
        repairIssues: [],
        belowPreferredBeatIds: [],
        route: 'BLOCKED',
        repairCallsUsed: 0,
        preInspectorRepairedBeatIds: [],
        blockedReason: parsed.reason,
      },
    });
  };

  let generated = parseInitial();

  onDeveloperLog?.('[FOUR BEAT ASSEMBLY]', buildFourBeatAssemblyDiagnostic(generated));
  onDeveloperLog?.('[FOUR BEAT TOTAL WORDS]', {
    totalWords: generated.totalWordCount,
    beatWordCounts: generated.beats.map((beat) => ({ beatId: beat.beatId, wordCount: beat.wordCount })),
    minimumAcceptedWords: contract.minimumAcceptedWords,
  });

  let gate = evaluateFourBeatGate(generated, contract, repairCallsUsed);
  onDeveloperLog?.('[FOUR BEAT STRUCTURAL VALIDATION]', {
    structurallyValid: gate.structurallyValid,
    route: gate.route,
    beatStatuses: gate.beats.map((beat) => ({ beatId: beat.beatId, status: beat.status })),
    blockedIssues: gate.issues.filter((issue) => issue.severity === 'BLOCK').map((issue) => issue.code),
    repairIssues: gate.repairIssues.map((issue) => issue.code),
    warnings: gate.warnings.map((issue) => issue.code),
  });

  if (gate.belowPreferredBeatIds.length > 0) {
    onDeveloperLog?.('[FOUR BEAT TARGET WARNING]', {
      belowPreferredBeats: gate.belowPreferredBeatIds,
      totalWords: generated.totalWordCount,
      blocked: false,
    });
  }

  if (!gate.structurallyValid) {
    throw new FourBeatStructuralFailureError(gate.blockedReason || 'Four-beat structure invalid.', {
      chapterNumber: chapterId,
      gate,
      partialContent: generated.content,
    });
  }

  onUserProgress?.('Four chapter beats drafted.');

  const repairCandidates = collectPreInspectorRepairCandidates(gate);
  onDeveloperLog?.('[FOUR BEAT REPAIR CANDIDATES]', repairCandidates.map((beat) => ({
    beatId: beat.beatId,
    sequence: beat.sequence,
    repairCodes: beat.issues.filter((issue) => issue.severity === 'REPAIR').map((issue) => issue.code),
  })));

  let remainingCandidateBeatIds = repairCandidates.map((beat) => beat.beatId);
  const preInspectorRepairedBeatIds: string[] = [];

  for (const target of repairCandidates) {
    if (!repairBeat || repairCallsUsed >= FOUR_BEAT_MAX_REPAIR_CALLS) break;

    const attempts = repairsAttempted.get(target.beatId) || 0;
    if (attempts >= FOUR_BEAT_MAX_REPAIR_PER_BEAT) {
      continue;
    }

    const contractBeat = contract.beats.find((beat) => beat.beatId === target.beatId);
    if (!contractBeat) continue;

    const previousAccepted = gate.beats
      .filter((beat) => beat.sequence < contractBeat.sequence && beat.status === 'ACCEPTED')
      .sort((a, b) => b.sequence - a.sequence)[0];
    const nextBeat = contract.beats.find((beat) => beat.sequence === contractBeat.sequence + 1);

    if (contractBeat.function === 'DECISION_REVERSAL') {
      onUserProgress?.('Strengthening the chapter\u2019s decisive turn...');
    }

    const preRepairStatus = target.status;

    onDeveloperLog?.('[FOUR BEAT REPAIR]', {
      beatId: target.beatId,
      sequence: target.sequence,
      issues: target.issues.filter((issue) => issue.severity === 'REPAIR'),
      attempt: attempts + 1,
    });

    const deficientBeat =
      generated.beats.find((beat) => beat.beatId === target.beatId) || {
        beatId: target.beatId,
        sequence: target.sequence,
        content: target.content,
        endingState: target.endingState,
        wordCount: target.wordCount,
        requiredEventsSatisfied: target.requiredEventsSatisfied,
        decisionPresent: target.decisionPresent,
        consequencePresent: target.consequencePresent,
        truncated: target.truncated,
      };

    const repaired = await repairBeat({
      contract,
      contractBeat,
      deficientBeat,
      classifiedBeat: target,
      previousAcceptedEndingState: previousAccepted?.endingState,
      nextBeatOpeningState: nextBeat?.openingState,
      storyContext: '',
    });

    repairCallsUsed += 1;
    repairsAttempted.set(target.beatId, attempts + 1);
    preInspectorRepairedBeatIds.push(target.beatId);

    const postRepairBeat = classifySingleGeneratedBeat(repaired.beat, contractBeat);
    const acceptedAfterRepair =
      postRepairBeat.status === 'ACCEPTED' ||
      !postRepairBeat.issues.some((issue) => issue.severity === 'REPAIR');

    const updatedBeats = generated.beats.map((beat) =>
      beat.beatId === repaired.beat.beatId ? repaired.beat : beat,
    );
    generated = assembleFourBeatChapter({
      chapterId,
      title: generated.title,
      summary: generated.summary,
      beats: updatedBeats,
    });

    remainingCandidateBeatIds = remainingCandidateBeatIds.filter((beatId) => beatId !== target.beatId);

    onDeveloperLog?.('[FOUR BEAT REPAIR RESULT]', {
      beatId: target.beatId,
      preRepairStatus,
      postRepairStatus: postRepairBeat.status,
      remainingCandidateBeatIds: [...remainingCandidateBeatIds],
      acceptedAfterRepair,
    });
  }

  gate = evaluateFourBeatGate(generated, contract, repairCallsUsed);
  gate = { ...gate, preInspectorRepairedBeatIds };
  onDeveloperLog?.('[FOUR BEAT FINAL GATE]', {
    structurallyValid: gate.structurallyValid,
    route: gate.route,
    accepted: gate.accepted,
    repairCallsUsed,
    beatStatuses: gate.beats.map((beat) => ({ beatId: beat.beatId, status: beat.status })),
    repairIssues: gate.repairIssues.map((issue) => issue.code),
    warnings: gate.warnings.map((issue) => issue.code),
  });

  if (!gate.structurallyValid) {
    throw new FourBeatStructuralFailureError(gate.blockedReason || 'Four-beat structure invalid after repair.', {
      chapterNumber: chapterId,
      gate,
      partialContent: generated.content,
    });
  }

  if (gate.repairIssues.length > 0 && collectPreInspectorRepairCandidates(gate).length > 0) {
    throw new FourBeatStructuralFailureError(
      'One or more chapter beats could not be repaired safely.',
      {
        chapterNumber: chapterId,
        gate,
        partialContent: generated.content,
      },
    );
  }

  onDeveloperLog?.('[FOUR BEAT ASSEMBLY]', {
    ...buildFourBeatAssemblyDiagnostic(generated),
    route: gate.route,
    belowPreferredBeats: gate.belowPreferredBeatIds,
    totalWords: generated.totalWordCount,
  });
  onUserProgress?.(`Four-beat chapter assembled: ${generated.totalWordCount} words.`);

  return { generated, gate };
}

export function getAcceptedBeatsForRepairContext(gate: FourBeatGateResult): ClassifiedChapterBeat[] {
  return gate.beats.filter((beat) => beat.status === 'ACCEPTED');
}
